import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { DocumentState } from "@/services/backend/documents/types/state";
import { backendApi } from "@/services/backend/api";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { Lst } from "@/shared/lib/lst/lst";
import { PositionPatch } from "@/services/backend/htz/position/types/position";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const htzConfirmationApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzConfirmation"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzConfirmationCreate: build.mutation<Confirmation, CreateRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "HtzConfirmation", id: request.id },
          { type: "HtzConfirmation", id: "LIST" },
        ],
      }),
      htzConfirmationAddPosition: build.mutation<
        Confirmation,
        AddPositionRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/position`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "HtzConfirmation", id: request.id },
          { type: "HtzConfirmation", id: "LIST" },
        ],
      }),
      htzConfirmationPatchPosition: build.mutation<
        Confirmation,
        PatchPositionRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/position`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "HtzConfirmation", id: request.id },
          { type: "HtzConfirmation", id: "LIST" },
        ],
      }),
      htzConfirmationRemovePosition: build.mutation<
        Confirmation,
        RemovePositionRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/position`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "HtzConfirmation", id: request.id },
          { type: "HtzConfirmation", id: "LIST" },
        ],
      }),
      htzConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          "HtzConfirmation",
        ],
      }),
      htzConfirmationShow: build.query<Confirmation, ShowRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzConfirmation"),
      }),
      htzConfirmationList: build.query<Lst<Confirmation>, ListRequest>({
        query: (request) => ({
          url: `/htz/confirmation/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.items, "HtzConfirmation"),
      }),
      htzConfirmationResetReferenceNumber: build.mutation<
        Confirmation,
        ResetReferenceNumberRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/reset-reference-number`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          { type: "HtzConfirmation", id: "LIST" },
        ],
      }),
      htzConfirmationUpdateState: build.mutation<
        Confirmation,
        UpdateStateRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation/state`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_r, _e, request) => [
          { type: "HtzConfirmation", id: request.id },
          "HtzConfirmation",
        ],
      }),
      htzConfirmationDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["HtzConfirmation"],
      }),
      htzConfirmationPdfPreview: build.query<
        { objectURL: string },
        PreviewPDFRequest
      >({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: request,
          headers: { Accept: "application/pdf" },
          responseHandler: objectURLResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzConfirmationPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: `/htz/confirmation`,
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
        invalidatesTags: (_r, _e, request) => [
          { type: "HtzConfirmation", id: request.confirmationId },
        ],
      }),
    }),
  });

export const {
  useHtzConfirmationCreateMutation,
  useHtzConfirmationAddPositionMutation,
  useHtzConfirmationPatchPositionMutation,
  useHtzConfirmationRemovePositionMutation,
  useHtzConfirmationPatchMutation,
  useHtzConfirmationShowQuery,
  useHtzConfirmationResetReferenceNumberMutation,
  useHtzConfirmationListQuery,
  useHtzConfirmationUpdateStateMutation,
  useHtzConfirmationDeleteMutation,
  useLazyHtzConfirmationPdfPreviewQuery,
  useHtzConfirmationPdfDownloadMutation,
} = htzConfirmationApi;

export type PatchRequest = {
  id: string;
  referenceNumber?: string;
  specialAgreements?: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export enum ConfirmationDocument {
  ALL = "all",
  CONFIRMATION = "confirmation",
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface PreviewPDFRequest {
  confirmationId: string;
  document: ConfirmationDocument;
}

export interface PDFRequest {
  confirmationId: string;
  document: ConfirmationDocument;
  download: boolean;
  downloadBy: string;
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateHtzConfirmationCache = updateCache(
  htzConfirmationApi,
  "htzConfirmationShow",
);

/**
 * CreateRequest identifies a new confirmation. Everything else is a default or
 * set with a {@link PatchRequest}.
 */
export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

// AddPositionRequest adds the position `positionId` to the confirmation `id`,
// prefilled with the patch, e.g. from a catalog item.
export interface AddPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

// PatchPositionRequest updates the position `positionId` of the confirmation
// `id`.
export interface PatchPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

export interface RemovePositionRequest {
  id: string;
  positionId: string;
}
